import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, Ticket, Instagram, Youtube } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import VideoPlayer from "@/components/VideoPlayer";
import { publicUrl } from "@/lib/storage";
import { athleteImageUrl, athletePlaceholder } from "@/lib/athleteImage";
import CampaignReturnLink from "@/components/CampaignReturnLink";

type Team = {
  id: string;
  slug: string;
  name: string;
  sport: string | null;
  description: string | null;
  logo_path: string | null;
  image_paths: string[];
  video_url: string | null;
  website_url: string | null;
  tickets_url: string | null;
  shop_url: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  youtube_url: string | null;
};

type AthleteRow = {
  id: string;
  slug: string;
  name: string;
  sport: string;
  country: string | null;
  tagline: string | null;
  image_path: string | null;
};

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M16.5 3a5.5 5.5 0 0 0 5 4.5v3.1a8.6 8.6 0 0 1-5-1.6v6.7a6.3 6.3 0 1 1-6.3-6.3c.3 0 .6 0 .9.1v3.2a3.2 3.2 0 1 0 2.2 3V3h3.2z" />
  </svg>
);

// Printed/campaign-friendly URLs may use a corrected spelling of a stored slug.
const SLUG_ALIASES: Record<string, string> = {
  "jersey-shore-wave": "jersy-shore-wave",
};

const WAVE_SLUG = "jersy-shore-wave";

const TeamDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [team, setTeam] = useState<Team | null>(null);
  const [roster, setRoster] = useState<AthleteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      const resolvedSlug = SLUG_ALIASES[slug] ?? slug;
      const { data: t } = await supabase
        .from("teams")
        .select("*")
        .eq("slug", resolvedSlug)
        .eq("published", true)
        .maybeSingle();
      if (!t) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setTeam(t as any);

      const { data: links } = await supabase
        .from("athlete_teams")
        .select("athlete_id")
        .eq("team_id", (t as any).id);
      const ids = (links ?? []).map((l: any) => l.athlete_id);
      if (ids.length) {
        const { data: aths } = await supabase
          .from("athletes")
          .select("id,slug,name,sport,country,tagline,image_path")
          .in("id", ids)
          .eq("published", true)
          .order("sort_order");
        setRoster((aths as any) ?? []);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (notFound || !team) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-center">
          <h1 className="font-display text-4xl mb-4">Team not found</h1>
          <Link to="/teams" className="text-primary underline">Back to teams</Link>
        </div>
      </div>
    );
  }

  const logo = publicUrl(team.logo_path) ?? null;
  const gallery = (team.image_paths ?? []).map((p) => publicUrl(p)).filter(Boolean) as string[];
  const descIsHtml = !!team.description && /<\/?[a-z][\s\S]*>/i.test(team.description);

  const isWave = team.slug === WAVE_SLUG;
  const categoryLabel = isWave ? "Women’s tackle football" : team.sport;

  const links: { url: string | null; label: string; Icon: any }[] = [
    { url: team.website_url, label: "Website", Icon: Globe },
    { url: team.tickets_url, label: "Tickets", Icon: Ticket },
    { url: team.instagram_url, label: "Instagram", Icon: Instagram },
    { url: team.tiktok_url, label: "TikTok", Icon: TikTokIcon },
    { url: team.youtube_url, label: "YouTube", Icon: Youtube },
  ].filter((l) => !!l.url);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="container py-8 md:py-12">
          <nav aria-label="Team navigation" className="flex min-w-0 flex-col items-start gap-1">
            <CampaignReturnLink />
            <Link
              to="/teams"
              className="inline-flex min-h-[44px] max-w-full items-center text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              ← Back to teams
            </Link>
          </nav>
          <div className="mt-5 flex flex-col md:flex-row items-center md:items-center gap-5 md:gap-8">
            {logo && (
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-3xl bg-muted overflow-hidden shrink-0 shadow-bold">
                <img src={logo} alt={`${team.name} logo`} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="text-center md:text-left">
              {categoryLabel && (
                <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                  {categoryLabel}
                </p>
              )}
              <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">{team.name}</h1>
              {links.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2 justify-center md:justify-start">
                  {links.map(({ url, label, Icon }) => (
                    <a
                      key={label}
                      href={url!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </a>
                  ))}
                </div>
              )}
              {isWave && (
                <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
                  <Link
                    to="/wave#roster"
                    className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-wave text-wave-foreground font-semibold shadow-bold hover:bg-wave/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wave focus-visible:ring-offset-2"
                  >
                    Meet the athletes <span aria-hidden>→</span>
                  </Link>
                  <Link
                    to="/wave"
                    className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full border border-wave text-wave font-semibold hover:bg-wave/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wave focus-visible:ring-offset-2"
                  >
                    Know her name. Back her game.
                  </Link>
                </div>
              )}
              <div className="mt-4 flex justify-center md:justify-start">
                {team.shop_url ? (
                  <a
                    href={team.shop_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold shadow-bold hover:bg-secondary hover:text-secondary-foreground transition-colors"
                  >
                    Shop Our Gear
                    <span aria-hidden>→</span>
                  </a>
                ) : (
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold shadow-bold hover:bg-secondary hover:text-secondary-foreground transition-colors"
                  >
                    Shop Our Gear
                    <span aria-hidden>→</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        {(team.description || isWave) && (
          <section className="container pb-10 md:pb-12 max-w-3xl">
            <h2 className="font-display text-3xl mb-4">{isWave ? "About the Wave" : "About"}</h2>
            {isWave && (
              <p className="serif text-lg md:text-xl text-foreground leading-relaxed mb-4">
                The Jersey Shore Wave is a women’s tackle football team competing in the Women’s
                National Football Conference. The team brings high-level women’s football to the
                Jersey Shore and is building a community where athletes can compete, lead and grow.
              </p>
            )}
            {team.description && (
              <div className="text-muted-foreground leading-relaxed text-base md:text-lg">
                {descIsHtml ? <RichText html={team.description} /> : <p>{team.description}</p>}
              </div>
            )}
          </section>
        )}

        {/* Why the Wave matters */}
        {isWave && (
          <section className="container pb-10 md:pb-12">
            <div className="rounded-3xl bg-secondary text-secondary-foreground px-6 py-8 md:px-10 md:py-10">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-wave">
                Why the Wave matters
              </span>
              <h2 className="font-display text-3xl md:text-4xl mt-2 text-balance">
                A place to compete, and a platform to be seen.
              </h2>
              <ul className="mt-6 grid gap-4 sm:grid-cols-3">
                <li className="rounded-2xl bg-background text-foreground p-5 border-t-4 border-wave">
                  <h3 className="font-display text-xl">A place to compete</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    The Wave gives women a place to compete at a high level.
                  </p>
                </li>
                <li className="rounded-2xl bg-background text-foreground p-5 border-t-4 border-wave">
                  <h3 className="font-display text-xl">Visibility and opportunity</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    The team helps create visibility and opportunity for women’s football.
                  </p>
                </li>
                <li className="rounded-2xl bg-background text-foreground p-5 border-t-4 border-wave">
                  <h3 className="font-display text-xl">A team, not just profiles</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    The athletes are part of a larger team and community, not just individual
                    profiles.
                  </p>
                </li>
              </ul>
            </div>
          </section>
        )}

        {/* Video */}
        {team.video_url && (
          <section className="container pb-16">
            <h2 className="font-display text-3xl mb-6">Watch</h2>
            <VideoPlayer url={team.video_url} />
          </section>
        )}

        {/* Gallery */}
        {gallery.length > 0 && (
          <section className="container pb-16">
            <h2 className="font-display text-3xl mb-6">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {gallery.map((g, i) => (
                <a
                  key={i}
                  href={g}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square rounded-2xl overflow-hidden bg-muted block group"
                >
                  <img
                    src={g}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Roster */}
        {roster.length > 0 && (
          <section id="teammates" className="bg-gradient-warm py-10 md:py-14 scroll-mt-20">
            <div className="container">
              <h2 className="font-display text-3xl mb-6">Teammates</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {roster.map((a) => (
                  <Link
                    key={a.id}
                    to={`/athlete/${a.slug}`}
                    className="group block rounded-2xl overflow-hidden bg-card shadow-card hover:shadow-bold transition-all hover:-translate-y-1"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={athleteImageUrl(a.image_path)}
                        alt={a.name}
                        loading="lazy"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = athletePlaceholder;
                        }}
                        className="w-full h-full object-cover bg-muted transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-display text-base leading-tight truncate">{a.name}</h3>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                        {a.sport}
                        {a.country ? ` · ${a.country}` : ""}
                      </p>
                      <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:underline">
                        Meet {a.name.split(" ")[0]} <span aria-hidden>→</span>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Support distinction + team CTA */}
        {isWave && (
          <section className="container py-10 md:py-14">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl text-balance">
                Backing an athlete vs. backing the team
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Support for an individual athlete is separate from team sponsorship. Fans can choose
                the athlete whose story they connect with and support her directly through her
                approved SIIS profile. For commercial partnerships, appearances or brand
                opportunities, contact the team or use{" "}
                <Link to="/contact" className="text-wave underline underline-offset-2 hover:text-wave/80">
                  Work with the athletes
                </Link>
                .
              </p>
              <div className="mt-8 rounded-3xl border border-wave/40 bg-wave/5 px-6 py-6 md:px-8">
                <h3 className="font-display text-2xl">Want to support the Wave as a team?</h3>
                <div className="mt-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-wave text-wave-foreground font-semibold hover:bg-wave/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wave focus-visible:ring-offset-2"
                  >
                    Work with the Wave <span aria-hidden>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TeamDetail;
